/**
 * KeyboardCheatSheet — global help modal triggered by `?`.
 *
 * Lists the keyboard shortcuts the app supports. Editorial typography (serif
 * heading, sans tabular nums for the key bindings, hairline border surface).
 */
import * as Dialog from '@radix-ui/react-dialog';
import { X } from '@phosphor-icons/react';
import { cn } from '../lib/cn';

interface ShortcutRow {
  keys: string;
  description: string;
}

const SHORTCUTS: ShortcutRow[] = [
  { keys: 'Cmd / Ctrl + K', description: 'Capture an entry' },
  { keys: 'Cmd / Ctrl + Shift + H', description: 'Switch households' },
  { keys: '/', description: 'Search your places' },
  { keys: '?', description: 'Show this list' },
  { keys: 'Esc', description: 'Close the active sheet' },
];

export interface KeyboardCheatSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function KeyboardCheatSheet({
  open,
  onOpenChange,
}: KeyboardCheatSheetProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-[80]',
            'bg-bg/85 backdrop-blur-md',
            'data-[state=open]:animate-[fade-in_240ms_var(--ease-out-slow)]',
          )}
        />
        <Dialog.Content
          aria-describedby={undefined}
          className={cn(
            'fixed left-1/2 top-1/2 z-[80] -translate-x-1/2 -translate-y-1/2',
            'w-[min(92vw,480px)] max-h-[80vh] overflow-y-auto',
            'bg-surface border border-ink-200',
            'p-6 md:p-8',
            'focus:outline-none',
          )}
        >
          <header className="mb-6 flex items-baseline justify-between gap-4">
            <div>
              <p className="font-sans text-xs uppercase tracking-[0.14em] text-ink-500">
                Shortcuts
              </p>
              <Dialog.Title className="mt-1 font-serif text-2xl text-ink-900 leading-tight">
                Keyboard shortcuts.
              </Dialog.Title>
            </div>
            <Dialog.Close
              aria-label="Close shortcuts"
              className={cn(
                'p-2 text-ink-700 hover:text-ink-900',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent',
              )}
            >
              <X size={18} weight="regular" />
            </Dialog.Close>
          </header>

          <dl className="divide-y divide-ink-100">
            {SHORTCUTS.map((s) => (
              <div
                key={s.keys}
                className="flex items-baseline justify-between gap-6 py-3"
              >
                <dt className="font-serif text-base text-ink-900 leading-snug">
                  {s.description}
                </dt>
                <dd className="font-sans text-xs uppercase tracking-[0.12em] text-ink-700 tabular-nums">
                  {s.keys}
                </dd>
              </div>
            ))}
          </dl>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
